// ---------------------------------------------------------------------------
// Template Loader — loads v2 Coogo JSON templates from /public/apps/{id}/
//
// v2 format: app.json manifest + 6-file blocks (ui/meta/state/events/flow/data.json)
// + design-system.json at app root.
// ---------------------------------------------------------------------------

import type {
  App,
  BlockDef,
  Flow,
  FlowMap,
} from '@cogo/types-v2'
import type { LoadedApp, DesignTokens } from '@/features/page-editor/store/types'
import type { FlowDiagramData } from '@/features/flow-editor/services/flow-diagram'
import templateCatalogJson from '@cogo/schema/template-catalog' with { type: 'json' }
import { tryDeriveThemeTokensFromDesignSystem } from '@cogo/design-presets'
import { fetchPresetTokensForEditor } from './preset-fetch'
import { applianceHidesCommerceMarker } from '@/services/appliance-commerce-surface'

// ---------------------------------------------------------------------------
// Template catalog — dynamic list from template-catalog.json
// ---------------------------------------------------------------------------

export interface TemplateSummary {
  id: string
  name: string
  description: string
  pages: number
  blocks: string[]
  domain: string
  tags: string[]
  thumbnail?: string
  tier?: 'A' | 'B'
  category?: string
}

const catalog = templateCatalogJson as {
  templates: Array<{
    id: string
    name: string
    description: string
    pages: number
    blocks: string[]
    domain: string
    tags: string[]
    thumbnail?: string
    tier?: 'A' | 'B'
    category?: string
  }>
}

const LOCAL_STUDIO_TEMPLATE_THUMBNAILS = new Set<string>([
  // Parked 2026-08-21 with apps-backup-20260821.
])

// Templates excluded from the catalog under an appliance distribution.
// Measured live-reachable by the 2026-08-18 full-feature walk
// (docs/superpowers/specs/2026-08-18-appliance-studio-full-walk.md §3): the 3
// `-new3` ecommerce Starters (coffee-shop/pet-supply/tech-gadget) plus the raw
// untranslated "ecommerce" domain-filter chip they produce on
// `/studio/templates` — the domain-chip row derives from whatever domains are
// present in `getTemplateCatalog()`'s output (`explore-gallery.tsx`), so
// filtering here removes both the cards and the chip from one place.
//
// The commerce marker set is NOT declared here any more: it is shared with
// every other template inventory through
// `@/services/appliance-commerce-surface`, so `domain` (this catalog),
// `category` (FEATURED_TEMPLATES) and `kind` (TEMPLATE_REGISTRY) all answer
// to one vocabulary and one distribution predicate.
export function getTemplateCatalog(): TemplateSummary[] {
  return catalog.templates
    .filter(isModernStudioTemplate)
    .filter((t) => !applianceHidesCommerceMarker(t.domain))
    .map((t) => ({
      id: t.id,
      name: t.name,
      description: t.description,
      pages: t.pages,
      blocks: t.blocks,
      domain: t.domain,
      tags: t.tags,
      thumbnail: thumbnailForModernStudioTemplate(t.id),
      ...(t.tier && { tier: t.tier }),
      category: t.category ?? t.domain,
    }))
}

/**
 * The catalogue's own declared domain for `templateId`, or `undefined` when
 * the id names nothing the catalogue knows. Unknown ids are NOT treated as
 * commerce: absence of a declaration is not a declaration, and a caller
 * loading an unlisted id fails on its own 404 a few lines below.
 */
function templateDomain(templateId: string): string | undefined {
  return catalog.templates.find((t) => t.id === templateId)?.domain
}

function isModernStudioTemplate(template: (typeof catalog.templates)[number]): boolean {
  return Boolean(thumbnailForModernStudioTemplate(template.id))
}

function thumbnailForModernStudioTemplate(templateId: string): string | undefined {
  if (!LOCAL_STUDIO_TEMPLATE_THUMBNAILS.has(templateId)) return undefined
  return `/thumbnails/${templateId}.png`
}

// ---------------------------------------------------------------------------
// loadTemplate — loads a complete v2 app from /apps/{templateId}/
// ---------------------------------------------------------------------------

export async function loadTemplate(
  templateId: string,
  opts?: { designSystemPresetId?: string | null },
): Promise<LoadedApp> {
  return (await loadTemplateWithBundle(templateId, opts)).app
}

/**
 * Same fetch/assemble work as {@link loadTemplate}, additionally returning the
 * bundle-relative `files` record it built on the way (the exact parsed JSON of
 * every file served under `/apps/{templateId}/`, keyed the way
 * `@cogo/template`'s `AppBundle` keys them).
 *
 * Exists so a caller that needs to identify WHICH template bytes a project was
 * seeded from (`features/templates/starter-lineage.ts`) can hash the same
 * bundle this load produced, without a second round of fetches. `loadTemplate`
 * is a thin wrapper over this, so every existing caller is byte-for-byte
 * unaffected.
 */
export async function loadTemplateWithBundle(
  templateId: string,
  opts?: { designSystemPresetId?: string | null },
): Promise<{ app: LoadedApp; files: Record<string, unknown> }> {
  // Removal must be complete, not catalogue-deep. `getTemplateCatalog()`
  // decides what a person can SEE; this decides what can be LOADED, and the
  // two are reached by different callers: `openProject(id)` and
  // `createServerProject(name, templateId)` (store/project-slice.ts) take an
  // id straight from a URL (`/studio/new?t=…`), a stored project, or a WebMCP
  // tool call (`cogo.studio.openProject`, `cogo.studio.remix_cogo_template`)
  // — none of which passes through the catalogue that hid the card. Refusing
  // here is what makes the hidden Starters unreachable rather than merely
  // unlisted, and it refuses by the SAME marker + distribution predicate the
  // catalogue filter uses, so the two can never disagree.
  if (applianceHidesCommerceMarker(templateDomain(templateId))) {
    throw new Error(
      `Template "${templateId}" is a commerce template and is not available on this appliance`,
    )
  }
  const basePath = `/apps/${templateId}`

  // 1. Load app manifest (v2: app.json)
  const manifestRes = await fetch(`${basePath}/app.json`)
  if (!manifestRes.ok) {
    throw new Error(`Failed to load manifest for ${templateId}: ${manifestRes.status}`)
  }
  const manifest: App = await manifestRes.json()

  // Assemble a bundle-relative `files` record exactly as @cogo/template's
  // AppBundle shapes it, by fetching the same candidate URLs `loadTemplate`
  // always fetched. Absent/failed fetches simply omit the key — the pure
  // mapper (`buildLoadedTemplateFromFiles`) treats a missing key exactly
  // like today's `res.ok === false` / `.catch(() => null)` fallbacks.
  const files: Record<string, unknown> = { 'app.json': manifest }

  // 2. Load design-system.json (v2 design tokens — className patterns)
  try {
    const dsRes = await fetch(`${basePath}/design-system.json`)
    if (dsRes.ok) files['design-system.json'] = await dsRes.json()
  } catch { /* design-system.json is optional */ }

  // 3. Collect all block names from pages + globalBlocks
  const blockNames = new Set<string>()
  if (manifest.globalBlocks) {
    for (const name of manifest.globalBlocks) blockNames.add(name)
  }
  for (const page of manifest.pages) {
    for (const name of page.blocks) blockNames.add(name)
  }

  // 4. Fetch blocks (discover sub-blocks via block-ref), populating `files`
  const loaded = new Set<string>()

  while (blockNames.size > loaded.size) {
    const toLoad = Array.from(blockNames).filter((n) => !loaded.has(n))
    if (toLoad.length === 0) break

    await Promise.all(
      toLoad.map(async (blockName) => {
        loaded.add(blockName)
        await fetchBlockFiles(basePath, blockName, files, blockNames)
      }),
    )
  }

  // 5. Fetch static datasources into files['data/<name>.json']-shaped keys
  if (manifest.datasources) {
    await Promise.all(
      Object.entries(manifest.datasources).map(async ([name, ds]) => {
        if (ds.type === 'static' && ds.path) {
          try {
            const dsPath = ds.path.startsWith('/') ? ds.path : `/${ds.path}`
            const res = await fetch(`${basePath}${dsPath}`)
            if (res.ok) files[dsPath.replace(/^\//, '')] = await res.json()
          } catch {
            console.warn(`Failed to load datasource: ${name}`)
          }
        }
      }),
    )
  }

  // 6. themePath fallback (priority 2 of the tokens resolution below) — the
  //    file is fetched unconditionally here (mirroring design-system.json's
  //    existing always-fetch-may-not-use pattern above) so the pure mapper
  //    can decide priority without doing any IO itself. NOTE: the original
  //    code fetched `${basePath}/${themePath}` (unconditional extra slash);
  //    for the bundle-relative key we normalize by stripping any leading
  //    slash from `themePath` itself, so a themePath value that already
  //    starts with '/' resolves to the same relative key a non-slashed
  //    value would (the double-slash URL quirk of the old fetch path is not
  //    reproduced — this is the "normalize consistently" case called out in
  //    the task brief).
  //
  //    Scope of the extra fetch (corrected — do not understate this as a
  //    "rare" case): this fetch is unconditional on `manifest.themePath`
  //    alone and has nothing to do with `opts?.designSystemPresetId`. It
  //    fires on the common "legacy direct-honor" path too — templates whose
  //    design-system.json carries literal `colors.primary` / `radius`
  //    fields instead of an `axes` block (see
  //    `tryDeriveThemeTokensFromDesignSystem` Layer 2 in
  //    `@cogo/design-presets`), which covers the majority of templates on
  //    disk. In that case `buildLoadedTemplateFromFiles` picks priority 1
  //    (axesTokens/legacy-direct) and this priority-2 fetch's result is
  //    discarded — the fetch still runs, it's just unused. The returned
  //    `LoadedApp` is unchanged either way (deep-equal proven against the
  //    pre-refactor output); the extra network call is a routine cost of
  //    this path, not a rare edge case, accepted here to keep
  //    `buildLoadedTemplateFromFiles` IO-free/pure.
  const themePath = (manifest as unknown as Record<string, string>).themePath
  if (themePath) {
    try {
      const themeRes = await fetch(`${basePath}/${themePath}`)
      if (themeRes.ok) {
        files[themePath.replace(/^\//, '')] = await themeRes.json()
      }
    } catch {
      // theme.json is optional — fall back to defaults
    }
  }

  const result = buildLoadedTemplateFromFiles(templateId, files)

  // Priority 0 of the tokens resolution: design_system_preset_id FK → fetch
  // from Worker (platform.cogo.xyz), 60 s cache. This is orthogonal to the
  // bundle's own files (it queries an external preset by id, not a
  // bundle-relative path) so it can never live inside the pure mapper —
  // applied here as an override, which is equivalent to the original
  // short-circuiting `if (!tokens) { ...axes/themePath... }` because the
  // pure mapper already computed the axes/themePath-derived fallback and
  // priority 0 always wins over priorities 1/2 when present.
  const presetId = opts?.designSystemPresetId
  if (presetId) {
    const presetTokens = await fetchPresetTokensForEditor(presetId)
    if (presetTokens) {
      result.tokens = presetTokens as DesignTokens
    }
  }

  return { app: result, files }
}

// ---------------------------------------------------------------------------
// buildLoadedTemplateFromFiles — pure bundle→LoadedApp mapping (no fetch)
//
// `files` uses bundle-relative paths exactly as @cogo/template's AppBundle:
// 'app.json', 'design-system.json', 'blocks/<id>/{ui,meta,state,events,flow,
// data}.json', 'data/<name>.json'. Values are already-parsed JS values
// (mirroring the `.json()` results the fetch path used to produce) — not
// raw JSON text. A missing key behaves exactly like today's failed-fetch /
// `res.ok === false` fallback (same defaults); presence of a key (even a
// falsy value like `null`) behaves like a successful fetch, matching the
// original `res?.ok ? await res.json() : default` check on availability,
// not on the value's truthiness.
// ---------------------------------------------------------------------------

export function buildLoadedTemplateFromFiles(
  templateId: string,
  files: Record<string, unknown>,
): LoadedApp {
  const manifest = files['app.json'] as App | undefined
  if (!manifest) {
    throw new Error(`Failed to load manifest for ${templateId}: missing app.json`)
  }

  const designSystem = files['design-system.json'] as Record<string, string> | undefined

  // Collect all block names from pages + globalBlocks
  const blockNames = new Set<string>()
  if (manifest.globalBlocks) {
    for (const name of manifest.globalBlocks) blockNames.add(name)
  }
  for (const page of manifest.pages) {
    for (const name of page.blocks) blockNames.add(name)
  }

  // Assemble blocks (discover sub-blocks via block-ref) — synchronous now
  // that every file is already resident in `files`.
  const blocks: Record<string, BlockDef> = {}
  const flows: Record<string, Flow> = {}
  const flowDiagram: Record<string, FlowDiagramData> = {}
  const loadedBlocks = new Set<string>()

  while (blockNames.size > loadedBlocks.size) {
    const toLoad = Array.from(blockNames).filter((n) => !loadedBlocks.has(n))
    if (toLoad.length === 0) break

    for (const blockName of toLoad) {
      loadedBlocks.add(blockName)
      assembleBlockFromFiles(files, blockName, blocks, flows, blockNames, flowDiagram)
    }
  }

  // Static datasources
  const data: Record<string, unknown> = {}
  if (manifest.datasources) {
    for (const [name, ds] of Object.entries(manifest.datasources)) {
      if (ds.type === 'static' && ds.path) {
        const dsKey = ds.path.replace(/^\//, '')
        if (dsKey in files) {
          data[name] = files[dsKey]
        }
      }
    }
  }

  // Design tokens — priority order (priority 0, the preset FK, is applied
  // by the caller since it requires an external fetch; see loadTemplate):
  //   1. design-system.json::axes (Studio Save flow output) → derived via
  //      @cogo/design-presets so the saved 6-axis selection drives the
  //      Studio editor preview the same way it drives the runtime.
  //   2. themePath JSON (legacy `resources/theme-default.json`) — silent
  //      fallback so apps without `axes` keep rendering as before.
  let tokens: DesignTokens | undefined
  const axesTokens = tryDeriveThemeTokensFromDesignSystem(designSystem)
  if (axesTokens) {
    tokens = axesTokens as unknown as DesignTokens
  } else {
    const themePath = (manifest as unknown as Record<string, string>).themePath
    if (themePath) {
      const themeKey = themePath.replace(/^\//, '')
      if (themeKey in files) {
        tokens = files[themeKey] as DesignTokens
      }
    }
  }

  return {
    manifest,
    blocks,
    data,
    flows,
    tokens,
    designSystem,
    ...(Object.keys(flowDiagram).length > 0 && { flowDiagram }),
  }
}

// ---------------------------------------------------------------------------
// fetchBlockFiles — fetches the v2 6-file block structure into `files`
// ---------------------------------------------------------------------------

async function fetchBlockFiles(
  basePath: string,
  blockName: string,
  files: Record<string, unknown>,
  blockNames: Set<string>,
): Promise<void> {
  const blockPath = `${basePath}/blocks/${blockName}`
  const relPath = `blocks/${blockName}`

  // ui.json is required
  const uiRes = await fetch(`${blockPath}/ui.json`)
  if (!uiRes.ok) {
    console.warn(`Block ${blockName}: ui.json not found`)
    return
  }
  const uiData = await uiRes.json()
  files[`${relPath}/ui.json`] = uiData

  // Optional files (loaded in parallel)
  const [metaRes, stateRes, eventsRes, flowRes, dataRes] = await Promise.all([
    fetch(`${blockPath}/meta.json`).catch(() => null),
    fetch(`${blockPath}/state.json`).catch(() => null),
    fetch(`${blockPath}/events.json`).catch(() => null),
    fetch(`${blockPath}/flow.json`).catch(() => null),
    fetch(`${blockPath}/data.json`).catch(() => null),
  ])

  if (metaRes?.ok) files[`${relPath}/meta.json`] = await metaRes.json()
  if (stateRes?.ok) files[`${relPath}/state.json`] = await stateRes.json()
  if (eventsRes?.ok) files[`${relPath}/events.json`] = await eventsRes.json()
  // flow.json parse is guarded on its own (matches the original pre-refactor
  // behavior): a malformed flow.json degrades this block's flow to `{}` via
  // the missing-key fallback in `assembleBlockFromFiles` instead of failing
  // the whole template load.
  if (flowRes?.ok) {
    try {
      files[`${relPath}/flow.json`] = await flowRes.json()
    } catch { /* malformed flow.json — degrade to {} via missing-key fallback */ }
  }
  if (dataRes?.ok) files[`${relPath}/data.json`] = await dataRes.json()

  // Discover sub-blocks (block-ref elements) so the outer while-loop in
  // loadTemplate knows to fetch them too.
  if (uiData.root) {
    discoverBlockRefs(uiData.root, blockNames)
  }
}

// ---------------------------------------------------------------------------
// assembleBlockFromFiles — pure equivalent of fetchBlockFiles, reading from
// an already-populated `files` record instead of issuing fetches.
// ---------------------------------------------------------------------------

function assembleBlockFromFiles(
  files: Record<string, unknown>,
  blockName: string,
  blocks: Record<string, BlockDef>,
  flows: Record<string, Flow>,
  blockNames: Set<string>,
  flowDiagram?: Record<string, FlowDiagramData>,
): void {
  const relPath = `blocks/${blockName}`
  const uiKey = `${relPath}/ui.json`

  // ui.json is required
  if (!(uiKey in files)) {
    console.warn(`Block ${blockName}: ui.json not found`)
    return
  }
  const uiData = files[uiKey] as { root?: unknown }

  const metaKey = `${relPath}/meta.json`
  const stateKey = `${relPath}/state.json`
  const eventsKey = `${relPath}/events.json`
  const flowKey = `${relPath}/flow.json`
  const dataKey = `${relPath}/data.json`

  // Build v2 BlockDef
  const blockDef: BlockDef = {
    meta: metaKey in files
      ? (files[metaKey] as BlockDef['meta'])
      : { name: blockName, type: 'ui' as const, version: '1.0.0', description: '', stateContract: { localState: {}, inputSymbols: [], outputSymbols: [] } },
    ui: uiData as BlockDef['ui'],
    events: eventsKey in files ? (files[eventsKey] as BlockDef['events']) : {},
    flow: {},
    // NOTE: the fallback literal below is intentionally NOT typed as
    // `BlockDef['state']` (BlockState has no `localState` field) — this
    // mirrors the original fetch path, where `await stateRes.json(): any`
    // absorbed the whole conditional into `any` and bypassed the excess-
    // property check. Preserved verbatim for observable parity (runtime
    // consumers read `.state.localState`, e.g. the `local.` prefix alias).
    state: (stateKey in files ? files[stateKey] : { localState: {} }) as unknown as BlockDef['state'],
    data: dataKey in files ? (files[dataKey] as BlockDef['data']) : {},
  }

  // Flow handling: merge into blockDef.flow and top-level flows map
  if (flowKey in files) {
    try {
      const flowData = files[flowKey] as { flows?: FlowMap; _diagram?: Record<string, FlowDiagramData> }
      const flowMap: FlowMap = flowData.flows ?? (flowData as unknown as FlowMap)
      blockDef.flow = flowMap
      const ns = blockDef.meta.namespace ?? blockName
      for (const [flowName, flow] of Object.entries(flowMap)) {
        flows[`${ns}.${flowName}`] = flow as Flow
        flows[flowName] = flow as Flow // backward compat for event bindings
      }
      // Pick up persisted diagram layout
      if (flowData._diagram && flowDiagram) {
        Object.assign(flowDiagram, flowData._diagram)
      }
    } catch { /* skip */ }
  }

  blocks[blockName] = blockDef

  // Discover sub-blocks (block-ref elements)
  if (uiData.root) {
    discoverBlockRefs(uiData.root as { type?: string; blockType?: string; children?: unknown[] }, blockNames)
  }
}

// ---------------------------------------------------------------------------
// discoverBlockRefs — finds block-ref elements in the tree
// ---------------------------------------------------------------------------

function discoverBlockRefs(
  element: { type?: string; blockType?: string; children?: unknown[] },
  blockNames: Set<string>,
): void {
  if (element.type === 'block-ref' && element.blockType) {
    blockNames.add(element.blockType)
  }
  if (Array.isArray(element.children)) {
    for (const child of element.children) {
      discoverBlockRefs(child as typeof element, blockNames)
    }
  }
}
